<template>
  <div class="profile-card container mt-0 mb-4 p-0">
    <!-- Header with gradient background and progress bar -->
    <div class="profile-header text-white p-3">
      <!-- Completion Info -->
      <div class="d-flex justify-content-between small mb-1">
        <div>Profile Completed {{ item.profile_completion_level }}%</div>
      </div>

      <!-- Progress Bar -->
      <div>
        <div class="progress bg-white" style="height: 6px">
          <div
            class="progress-bar"
            role="progressbar"
            :style="`width: ${item.profile_completion_level}%; background-color: #00bfff;`"
          ></div>
        </div>
      </div>

      <!-- Two Column Section -->
      <div class="d-flex align-items-center justify-content-between mt-3">
        <!-- Profile Picture (Left Column) -->
        <div class="col-md-6 position-relative text-center">
          <!-- Profile Picture -->
          <img
            :src="generateFullUrlWithToken(item.profile_picture)"
            class="rounded-circle profile-pic"
            onerror="this.onerror=null;this.onload=null;this.src='/img/user_default_pic.png';"
            alt="Profile Picture"
          />

          <!-- Overlay Label only when default image -->
          <div
            v-if="item.profile_picture === '/img/user_default_pic.png'"
            class="upload-overlay"
          >
            <input
              id="imageUpload"
              type="file"
              ref="profilefileref"
              name="image"
              accept="image/*"
              @change="selectedFile"
              style="display: none"
            />
            <label for="imageUpload" class="upload-label-inside"
              >Click here to upload photo</label
            >
          </div>

          <!-- Upload Component -->
          <div v-if="isImageSelected">
            <UploadDocument
              class="mt-3"
              :fileData="fileData"
              @closeModal="closeModal"
              :isProfilePic="true"
              v-on="$listeners"
              @upload="photoUpload"
            />
          </div>
        </div>

        <!-- Visibility Tip (Right Column) -->
        <div class="text-end" style="font-size: 12px">
          Finish your profile to get<br />
          {{ 100 - item.profile_completion_level }} % more visibility.
        </div>
      </div>
    </div>

    <div class="text-grey small mt-2" style="margin-left: 65%">
      <span v-if="!isEducationalType"
        >Candidate ID: <b>{{ item.display_uid }}</b></span
      >
      <span v-if="isEducationalType"
        >Student ID: <b>{{ item.display_uid }}</b></span
      >
    </div>

    <div class="px-4 pb-4">
      <h4 class="font-weight-bold-1 text-primary">
        {{ item.first_name }} {{ item.surname }}
        ({{ isEducationalType ? "Student" : candidateType }})
      </h4>
      <div class="d-flex flex-wrap justify-content-center gap-3 my-1">
        <button
          style="margin-right: 70px !important"
          class="btn bg-light-transparent button br-12 mr-3"
          @click="navigateToEditProfile()"
        >
          Edit Profile
        </button>
        <button
          class="button_1 btn bg-light-transparent button br-12"
          @click="viewProfile()"
        >
          View Profile
        </button>
      </div>
      <div class="brb-blue-light pb-2 mb-2"></div>
    </div>

    <!-- Info Section -->
    <div class="px-4 pb-4">
      <h6 class="font-weight-bold mb-3">Personal Info:</h6>
      <ul class="list-unstyled info-list">
        <li class="brb-blue-light pb-2 d-flex">
          <div class="me-1 mr-4">
            <span>Gender:</span>
            <strong class="text-secondary">
              {{ item.gender || "--" }}
            </strong>
          </div>
          <div>
            <span class="ml-2">Age:</span>
            <strong class="text-secondary">{{ item.age }} years</strong>
          </div>
        </li>
        <li class="brb-blue-light pb-2">
          <span>Nationality:</span>
          <strong class="text-secondary" v-if="item.nationality.length">
            {{ item.nationality.join(", ") || "--" }}
          </strong>
        </li>
        <li class="brb-blue-light pb-2">
          <span>Email:</span>
          <strong class="text-secondary">{{ item.email || "Not shared" }}</strong>
        </li>
        <li class="brb-blue-light pb-2">
          <span>Phone:</span>
          <strong class="text-secondary">
            {{ item.phone ? item.phone.replace("^", "-") : "Not shared" }}
          </strong>
        </li>
        <li class="brb-blue-light pb-2">
          <span>WhatsApp</span>
          <strong class="text-secondary">
            {{ item.whatsapp ? item.whatsapp.replace("^", "-") : "--" }}
          </strong>
        </li>
        <li class="brb-blue-light pb-2">
          <span>Location:</span>
          <strong class="text-secondary">
            {{ item?.location || "--" }}
          </strong>
        </li>
      </ul>

      <CRow class="pb-2">
        <CCol>
          <h5><span class="item-info" item.location></span></h5>
          <span
            class="step bg-secondary"
            v-if="item.retired_clinician === 'Yes'"
            v-c-tooltip="'Retired'"
            >R</span
          >
          <span
            class="step bg-secondary"
            v-if="item.volunteer === 'Yes'"
            v-c-tooltip="'Volunteer'"
            >V</span
          >
        </CCol>
      </CRow>
      <div class="small mt-3">
        Reg on:
        <span class="text-primary bold">{{ createdOnDate }}</span> · Last Login:
        <span class="text-primary bold">{{ lastLogin }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters, mapActions } from "vuex";
import {
  Role,
  getScope,
  CONSTANT as CONST,
  generateFullUrlWithToken,
} from "@/helpers/helper";
import UploadDocument from "@/containers/CandidateProfile/UploadDocument";

export default {
  name: "CandidateInfoUserView",
  props: {
    item: Object,
  },
  components: { UploadDocument },
  data() {
    return {
      isImageSelected: false,
      fileData: {},
      isFileError: false,
      currentUserRole: getScope(),
      Role,
      generateFullUrlWithToken,
    };
  },
  computed: {
    ...mapGetters(["candidateProfile", "uploaded"]),
    lastLogin() {
      return this.item.last_date_of_login
        ? moment(this.item.last_date_of_login).format("DD-MMM-YYYY")
        : "--";
    },
    createdOnDate() {
      return this.item.created_on
        ? moment(this.item.created_on).format("DD-MMM-YYYY")
        : "--";
    },
    candidateType() {
      return this.candidateProfile?.candidate_type_id === 46
        ? this.candidateProfile?.candidate_type?.comments
        : this.candidateProfile?.candidate_type?.candidate_type;
    },
    isEducationalType() {
      return (
        this.candidateProfile?.customer_type_id ===
        CONST.CUSTOMER_TYPES.educational_institute
      );
    },
  },
  watch: {
    uploaded() {
      if (this.uploaded) {
        const { customer_uid: customer } = this.candidateProfile;
        this.getUserMe({ customer });
        this.isImageSelected = false;
        this.fileData = {};
      }
    },
  },
  methods: {
    ...mapActions(["uploadPhoto", "getUserMe"]),
    selectedFile(event) {
      const file = event.target.files[0];
      if (file && Math.round(file.size / (1024 * 1024)) <= 2) {
        this.fileData = file;
        this.isImageSelected = true;
        this.isFileError = false;
      } else {
        this.$refs.profilefileref.value = "";
        this.isFileError = true;
      }
    },
    closeModal() {
      this.$refs.profilefileref.value = "";
      this.fileData = {};
      this.isImageSelected = false;
    },
    photoUpload(payload) {
      const { customer_uid, candidate_uid, user_id } = this.candidateProfile;
      this.uploadPhoto({
        customer_uid,
        candidate_uid,
        user_id,
        [payload.name]: payload.value,
        document_type: payload.document_type || 1,
        fileName: payload.document_name,
      });
      this.$store.commit("SET_UPLOADED");
    },
    navigateToEditProfile(block = 0) {
      this.$store.state.candidate.block = block;
      this.$router.push("/profile");
    },
    viewProfile() {
      this.$router.push(`/candidate/${this.candidateProfile.candidate_uid}`);
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap");

.profile-card {
  font-family: "Space Grotesk", sans-serif;
  border-radius: 10px;
  max-width: 350px;
  padding: 0.5rem;
  margin: 0 auto 0.5rem auto;
  font-size: 13px;
  background-color: white;
  overflow: hidden;
  height: 98%;
}

.profile-header {
  background: linear-gradient(
    301.35deg,
    #ff00ff -72.6%,
    #4c34e9 47.08%,
    #00dfff 123.13%
  );
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  padding: 0.5rem;
  text-align: center;
  height: 150px;
}

.profile-pic-upload-label {
  display: block;
  position: relative;
  width: fit-content;
  cursor: pointer;
}

.profile-pic {
  width: 80px;
  height: 80px;
  border: 4px solid white;
  object-fit: cover;
  margin: 0 auto;
  transition: box-shadow 0.2s;
  position: relative;
  z-index: 1;
}

.profile-pic-upload-label:hover .profile-pic {
  box-shadow: 0 0 0 2px #4c34e9;
}

.upload-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80px;
  height: 80px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(76, 52, 233, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: none; // allows clicking through to the label/input
}

.upload-label-inside {
  color: white;
  font-size: 10px;
  text-align: center;
  padding: 0 6px;
  pointer-events: auto; // so label is clickable
  cursor: pointer;
  line-height: 1.2;
}

.upload-label {
  font-size: 9px;
  color: black;
  text-decoration: underline;
}

.upload-label-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(76, 52, 233, 0.6);
  font-size: 11px;
  border-radius: 50%;
  pointer-events: none;
  text-align: center;
  padding: 0 6px;
}

.info-list li {
  margin-bottom: 4px;
  font-size: 12px;
  margin-right: -2px;
}

.info-list span {
  display: inline-block;
  width: 80px;
  font-size: 12px;
  color: #281861;
  // color: #6c757d;
}

.px-4,
.pb-4,
.mt-5 {
  padding-left: 0.5rem !important;
  padding-right: 0.5rem !important;
  padding-bottom: 0.5rem !important;
  margin-top: 0.5rem !important;
}

.small,
.text-muted,
.font-weight-bold,
h4,
h6,
p,
ul,
li {
  font-size: 12px !important;
}

.font-weight-bold-1 {
  font-size: 18px !important;
}

.button,
.button_1 {
  font-size: 13px !important;
  padding: 0.35rem 0.85rem !important;
  min-width: 90px;
  height: 32px;
  border-radius: 12px;
}

.button_1 {
  background: linear-gradient(
    249.32deg,
    #00dfff -33.55%,
    #4c34e9 51.72%,
    #ff00ff 138.57%
  ) !important;
  color: #fff !important;
  transition: background 0.3s ease, transform 0.2s ease;
}

.button_1:hover {
  background: linear-gradient(
    249.32deg,
    #ff00ff -33.55%,
    #4c34e9 51.72%,
    #00dfff 138.57%
  ) !important;
  transform: scale(1.02);
}

.d-flex.flex-wrap.justify-content-center.gap-3.my-1 > button:not(:last-child) {
  margin-right: 8px;
}
</style>
